import React, { useEffect, useState, useContext, useRef } from "react";
import { Table, Tabs } from "antd";
import qs from "qs";
import { of, from, Subject } from "rxjs";
import { debounceTime, switchMap, catchError, takeUntil, count } from "rxjs/operators";
import apiClientconops from "../../../ApiEndpoint/apiClientconops";
import apiUrlconops from "../../../ApiEndpoint/configconops";
import { ConopsContext } from "../../conops-hooks/ConopsContext";
import { SiGooglemaps } from "react-icons/si";


const { TabPane } = Tabs;

const ConopsDataTable = () => {
    const [siteData, setSiteData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 100;
    const { selectedOpenSearchValue, selectedState, selectedCounty, selectedCity, selectedFacility, facilityTableObject, setFacilityTableObject,
        registerId, setRegisterId, markerRegistryId, selectedFacilityType, setCountyObject, setSelectedFacility, refineSearchProjectObject } = useContext(ConopsContext);
    console.log(refineSearchProjectObject, "datatable")
    const siteColumns = [
        {
            title: "Facility Name",
            dataIndex: "FACILITY_NAME",
            key: "FACILITY_NAME",
            render: (text, record) => (
                <span
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                    onClick={() => {
                        setRegisterId(record.fullObject.REGISTRY_ID)
                        setFacilityTableObject([record.fullObject])
                        setSelectedFacility(record.fullObject.REGISTRY_ID)
                        setCountyObject({ county: record?.fullObject?.FAC_COUNTY, state: record?.fullObject?.FAC_STATE })
                    }}
                >
                    {text}
                </span>
            ),
        },
        { title: "Mapped", dataIndex: "MAPPED", key: "MAPPED" },
        { title: "Facility Type", dataIndex: "FAC_TYPE", key: "FAC_TYPE" },
        { title: "State", dataIndex: "STATE_NAME", key: "STATE_NAME" },
        { title: "County", dataIndex: "FAC_COUNTY", key: "FAC_COUNTY" },
        { title: "City Name", dataIndex: "FAC_CITY", key: "FAC_CITY" },
        { title: "Street Address", dataIndex: "FAC_STREET", key: "FAC_STREET" },
        { title: "ZIP Code", dataIndex: "FAC_ZIP", key: "FAC_ZIP" },
        { title: "Veolia Site", dataIndex: "IS_VEOLIA_SITE", key: "IS_VEOLIA_SITE" },
    ];

    // Persistent RxJS subjects using refs to prevent recreation on every render
    const searchSubjectRef = useRef(null);
    const destroySubjectRef = useRef(null);
    const subscriptionRef = useRef(null);

    // Initialize RxJS observable chain once on mount
    useEffect(() => {
        const search$ = new Subject();
        const destroy$ = new Subject();
        searchSubjectRef.current = search$;
        destroySubjectRef.current = destroy$;

        const subscription = search$.pipe(
            debounceTime(400),
            takeUntil(destroy$),
            switchMap(({ params, controller }) =>
                from(
                    apiClientconops.get(`${apiUrlconops}/search_facilities`, {
                        params,
                        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
                        signal: controller.signal,
                    })
                ).pipe(
                    catchError((error) => {
                        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
                            console.error("Error fetching facilities:", error);
                        }
                        return of(null);
                    })
                )
            )
        ).subscribe((response) => {
            if (response?.data?.data) {
                const responseObject = response?.data?.data;
                setFacilityTableObject(responseObject);
                const formattedData = response.data.data.map((item, index) => ({
                    key: index + 1,
                    FACILITY_NAME: item.FAC_NAME || "N/A",
                    FAC_TYPE: item.FAC_TYPE ? item.FAC_TYPE.split("_").join("&") : "N/A",
                    STATE_NAME: item.FAC_STATE || "N/A",
                    FAC_COUNTY: item.FAC_COUNTY || "N/A",
                    FAC_CITY: item.FAC_CITY || "N/A",
                    FAC_STREET: item.FAC_STREET || "N/A",
                    FAC_ZIP: item.FAC_ZIP || "N/A",
                    IS_VEOLIA_SITE: item?.IS_VEOLIA_SITE ? "YES" : null,
                    MAPPED: item?.LATITUDE_MEASURE ? <SiGooglemaps style={{ fontSize: "20px" }} /> : null,
                    fullObject: item,
                }));
                setSiteData(formattedData);
                setTotalCount(response.data.count || 0);
            } else {
                setSiteData([]);
                setTotalCount(0);
            }
            setLoading(false);
        });

        subscriptionRef.current = subscription;

        // Cleanup only on unmount
        return () => {
            destroy$.next();
            destroy$.complete();
            subscription.unsubscribe();
        };
    }, []);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedOpenSearchValue, selectedState, selectedCounty, selectedCity, selectedFacilityType, selectedFacility, markerRegistryId]);

    // Emit search params whenever dependencies change
    useEffect(() => {
        if (!searchSubjectRef.current) return;

        const controller = new AbortController();
        const offset = (currentPage - 1) * pageSize;
        const params = {
            offset,
            limit: pageSize,
            ...(selectedOpenSearchValue && { q: selectedOpenSearchValue }),
            ...(selectedState && { fac_state: selectedState }),
            ...(selectedCounty && { fac_county: selectedCounty }),
            ...(selectedCity && { fac_city: selectedCity }),
            ...(selectedFacilityType && { fac_type: selectedFacilityType }),
            ...(selectedFacility ? { registry_id: selectedFacility } : markerRegistryId ? { registry_id: markerRegistryId } : {}),
            ...(refineSearchProjectObject?.project_region && {
                project_region: refineSearchProjectObject.project_region,
            }),
            ...(refineSearchProjectObject?.project_state && {
                project_state: refineSearchProjectObject.project_state,
            }),
            ...(refineSearchProjectObject?.project_operator && {
                project_operator: refineSearchProjectObject.project_operator,
            }),
            ...(refineSearchProjectObject?.project_scope && {
                project_scope: refineSearchProjectObject.project_scope,
            }),
            ...(refineSearchProjectObject?.min_revenue && {
                min_revenue: refineSearchProjectObject.min_revenue,
            }),
            ...(refineSearchProjectObject?.max_revenue && {
                max_revenue: refineSearchProjectObject.max_revenue,
            }),
            ...(refineSearchProjectObject?.min_population && {
                min_population: refineSearchProjectObject.min_population,
            }),
            ...(refineSearchProjectObject?.max_population && {
                max_population: refineSearchProjectObject.max_population,
            }),
            ...(refineSearchProjectObject?.contract_end_end && {
                contract_end_end: refineSearchProjectObject.contract_end_end,
            }),
            ...(refineSearchProjectObject?.total_design_flow && {
                total_design_flow: refineSearchProjectObject.total_design_flow,
            }),
            ...(refineSearchProjectObject?.population_density && {
                population_density: refineSearchProjectObject.population_density,
            }),
        };

        setLoading(true);
        searchSubjectRef.current.next({ params, controller });

        return () => {
            controller.abort();
        };
    }, [currentPage, pageSize, selectedOpenSearchValue, selectedState, selectedCounty, selectedCity, selectedFacility, markerRegistryId, selectedFacilityType, refineSearchProjectObject]);

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
    };

    return (
        <Tabs defaultActiveKey="1">
            <TabPane tab="Facilities" key="1">
                <Table
                    columns={siteColumns}
                    dataSource={siteData}
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        total: totalCount,
                        pageSize,
                        showSizeChanger: false,
                    }}
                    onChange={handleTableChange}
                    scroll={{ y: 300 }}
                    tableLayout="auto"
                    bordered
                    rowClassName={(record) =>
                        record.fullObject.REGISTRY_ID === registerId ? 'highlighted-row' : ''
                    }
                />
            </TabPane>
        </Tabs>
    );
};

export default ConopsDataTable;
